/**
 * stage3_fx — shader wave stage 3 (2026-09-09): the engine's remaining
 * material/VFX features wired into Atlas, the PURE rules in one place so the
 * harness can pin them without a renderer:
 *
 *   interior mapping  · which material names are glass; the lit-window ratio
 *                       by the night factor (rooms light up after dusk)
 *   VFX pack          · dissolve progress on a dying creature, heat-haze
 *                       strength behind a hovercraft by speed, the shield
 *                       shell's intensity after a hit
 *   lit decals        · the crater/scorch normal-map oracle the bake samples
 *   lamp cookies      · the street-lamp head gobo (radial fall-off + louvres)
 *
 * The registries at the bottom are the runtime side: handle sets the
 * per-frame drivers write (interior litRatio, soft-particle camera far) and
 * the counters `__stage3()` reports.
 */

/** Tier flags added by stage 3 (each usable on any tier). */
export const STAGE3_FLAGS = ['interiorMapping', 'vfxShaders', 'litDecals', 'lampCookies', 'waterFoam', 'waterCaustics'] as const;
export type Stage3Flag = (typeof STAGE3_FLAGS)[number];

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (v: number): number => { const t = clamp01(v); return t * t * (3 - 2 * t); };

/** A cooked material that is a window pane / glass facade (the instancer's
 *  `#fade` clones are derived from the replaced material, never matched here). */
export function isGlassMaterialName(name: string | undefined | null): boolean {
  if (!name) return false;
  if (name.includes('#')) return false;
  return /glass|window|pane/i.test(name);
}

/** Rooms per UV tile for a glass material: a facade tiles many rooms, a single pane holds one. */
export function roomsForGlass(name: string): { roomsX: number; roomsY: number; roomDepth: number } {
  return /facade/i.test(name) ? { roomsX: 6, roomsY: 6, roomDepth: 0.8 } : { roomsX: 2, roomsY: 2, roomDepth: 1.0 };
}

/**
 * Lit-window ratio by the sky's night factor (0 day … 1 night): a few rooms
 * glow at dusk, most by full night; by day the rooms read dark behind the
 * glass (the daylight outshines them).
 */
export function litRatioForNight(night: number): number {
  return 0.08 + 0.62 * smooth((clamp01(night) - 0.05) / 0.6);
}

/** Dissolve progress 0..1 over `durS`: eased so the body holds, then burns away. */
export function dissolveProgress(tS: number, durS = 0.9): number {
  const u = clamp01(tS / Math.max(1e-3, durS));
  return u * u * (3 - 2 * u);
}
export const DISSOLVE_S = 0.9;

/**
 * Heat-haze distortion strength (screen-UV offset) behind a hovercraft: none
 * at rest, a shimmer from a walking pace, full at the cruise top.
 */
export function hazeStrength(speedMps: number, topMps: number): number {
  if (!(speedMps > 2.5) || !(topMps > 0)) return 0;
  return 0.012 * smooth((speedMps - 2.5) / Math.max(1, topMps - 2.5));
}

/** Shield shell intensity after a hit: a 1.6 flash decaying over ~half a second. */
export function shieldIntensity(sinceHitS: number): number {
  if (!(sinceHitS >= 0) || sinceHitS > 2) return 0;
  const v = 1.6 * Math.exp(-sinceHitS / 0.45);
  return v < 0.02 ? 0 : v;
}

/**
 * Normal-map oracle for the lit decals (u, v in 0..1 over the quad; +Z is out
 * of the surface). A bullet hole is a bowl with a raised lip, a blast a wide
 * crater with a rim, a scorch is flat with a faint pit — returned as a unit
 * tangent-space normal.
 */
export function decalNormal(kind: 'hole' | 'scorch' | 'blast', u: number, v: number): [number, number, number] {
  const x = u * 2 - 1, y = v * 2 - 1;
  const r = Math.hypot(x, y);
  // height field h(r): depth profile per kind
  const h = (rr: number): number => {
    if (kind === 'scorch') return -0.08 * smooth(1 - rr / 0.6);
    const depth = kind === 'blast' ? 0.9 : 0.6, bowl = kind === 'blast' ? 0.55 : 0.45, lip = kind === 'blast' ? 0.22 : 0.12;
    const pit = -depth * smooth(1 - rr / bowl);
    const rim = lip * smooth(1 - Math.abs(rr - bowl) / (kind === 'blast' ? 0.2 : 0.12));
    return pit + rim;
  };
  const e = 0.01;
  const dhdx = (h(Math.hypot(x + e, y)) - h(Math.hypot(x - e, y))) / (2 * e);
  const dhdy = (h(Math.hypot(x, y + e)) - h(Math.hypot(x, y - e))) / (2 * e);
  const nx = -dhdx, ny = -dhdy, nz = 1;
  const len = Math.hypot(nx, ny, nz);
  return [nx / len, ny / len, nz / len];
}

/**
 * Street-lamp head gobo (0..1): a soft disc with eight louvre spokes, so the
 * pool of light on the pavement carries the fixture's shape instead of a
 * plain cone.
 */
export function lampCookieValue(u: number, v: number): number {
  const x = u * 2 - 1, y = v * 2 - 1;
  const r = Math.hypot(x, y);
  const disc = smooth(1 - (r - 0.55) / 0.4);
  const a = Math.atan2(y, x);
  // Louvres only past the hub (atan2 at the centre would put a spoke through it).
  const spoke = 1 - 0.35 * (1 - Math.abs(Math.sin(a * 4))) * smooth((r - 0.2) / 0.15);
  return clamp01(disc * spoke);
}

// ── Runtime registries (drivers write, diag reads) ────────────────────────
export interface InteriorHandle { litRatio: { value: number } }
export const interiorHandles = new Set<InteriorHandle>();
export interface SoftHandle { softDistance: { value: number }; cameraFar: { value: number } }
export const softHandles = new Set<SoftHandle>();

export const stage3Stats = {
  interior: 0,          // glass materials replaced by the interior-mapping material
  soft: 0,              // materials carrying the soft-particle fade
  holograms: 0,         // hologram emblems standing over notice boards
  dissolves: 0,         // creature removals that dissolved (vs vanished)
  dissolving: 0,        // dissolves in flight right now
  cookies: 0,           // street-lamp spotlights wearing the cookie
  haze: 0,              // heat-haze strength last frame
  hazeQuads: 0,         // haze quads built
  shieldHits: 0,        // hull hits that flashed the shield
  shieldShells: 0,      // shield shells built
  foamUpdates: 0,       // FoamAccumulator grid refreshes
  causticRenders: 0,    // CausticsProjector renders
  litDecals: 0,         // decal materials built lit
};
